// PhoneVerificationModal.js
import React, { useState } from 'react';
import { Modal, Box, Typography, TextField, Button } from '@mui/material';
import {ReactComponent as Cancel} from '../assets/Cancel.svg';
import {ReactComponent as AlertIcon} from '../assets/AlertIcon.svg';
import { ServerURL } from '../serverURL';

const PhoneVerificationModal = ({
  isVerifyModalOpen,
  handleVerifyModalClose,
  isMobileNumModelOpen, // This will now control the Mobile Number Input Modal
  handleMobileNumModelOpen,
  handleMobileNumModelClose,
  // isVerificationCodeSent and setIsVerificationCodeSent can be removed or managed locally if needed
  // as the primary trigger will be opening the new OTP modal
}) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [otpMessage, setOtpMessage] = useState(''); // For messages in mobile number modal
  const [verificationStatus, setVerificationStatus] = useState(''); // For messages in OTP modal
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false); // New state for OTP modal
  const [isLoadingOtp, setIsLoadingOtp] = useState(false); // New state for loading indicator
  const [isLoadingVerification, setIsLoadingVerification] = useState(false); // New state for OTP verification loading

    const handleSendOtp = async () => {
    const authKey = localStorage.getItem('authKey');
    if (!authKey) {
      setOtpMessage('認証キーが見つかりません');
      setTimeout(() => setOtpMessage(''), 3000);
      return;
    }

    setIsLoadingOtp(true); // Set loading state to true
    setOtpMessage(''); // Clear previous messages immediately on new attempt

    const formData = new FormData();
    formData.append('mobile_number', mobileNumber);

    try {
      const response = await fetch(
        `${ServerURL.PRODUCTION_HOST_URL}candidates/mobile_number_verification`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authKey}`,
          },
          body: formData,
        }
      );
      const data = await response.json();

      if (response.ok) { // Check for a successful HTTP status code (2xx)
        setOtpMessage(data.message || 'OTPが正常に送信されました'); // Show success message from API or default
        // Wait for 5 seconds before closing the current modal and opening the next
        setTimeout(() => {
          handleMobileNumModelClose(); // Close the mobile number modal
          setIsOtpModalOpen(true); // Open the new OTP input modal
          setMobileNumber(''); // Clear mobile number after sending OTP
        }, 3000);
      } else {
        // Handle API failure response
        if (data.error && data.error.message) {
          setOtpMessage(data.error.message); // Display the specific error message from the API
        } else {
          setOtpMessage('OTPの送信に失敗しました'); // Generic failure message
        }
        setTimeout(() => setOtpMessage(''), 3000); // Hide message after 5s
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setOtpMessage('エラーが発生しました。もう一度お試しください。'); // General network/fetch error
      setTimeout(() => setOtpMessage(''), 3000);
    } finally {
      setIsLoadingOtp(false); // Reset loading state
    }
  };

  const fetchMobileNumberVerifiedStatus = async () => {
    const authKey = localStorage.getItem('authKey');
    if (!authKey) return;

    try {
      const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}candidates/mobile_number_verified_status`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authKey}`,
        },
      });
      const data = await response.json();
      const verified = data.candidate_sms_verified_status?.candidate_sms_is_verified;

      // Save value (0 or 1) in localStorage
      if (verified === 0 || verified === 1) {
        localStorage.setItem('candidate_sms_is_verified', verified);
      }
      // If you want, you can have a state variable for UI as well:
      // setIsMobileVerified(verified);
    } catch (error) {
      // setIsMobileVerified(0);
      localStorage.setItem('candidate_sms_is_verified', 0);
    }
  };

  const handleVerifyCode = async () => {
    const authKey = localStorage.getItem('authKey');
    if (!authKey) {
      setVerificationStatus('認証キーが見つかりません');
      setTimeout(() => setVerificationStatus(''), 3000);
      return;
    }

    setIsLoadingVerification(true); // Set loading state for verification
    setVerificationStatus(''); // Clear previous messages immediately on new attempt

    const formData = new FormData();
    formData.append('mobile_number_otp', verificationCode); // Changed parameter name here

    try {
      const response = await fetch(
        `${ServerURL.PRODUCTION_HOST_URL}candidates/mobile_number_otp_validation`, // Updated API endpoint
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authKey}`,
          },
          body: formData,
        }
      );
      const data = await response.json();

        if (response.ok) {
          setVerificationStatus(data.message || '認証に成功しました');
          fetchMobileNumberVerifiedStatus(); // <-- CALL IT HERE!
          setTimeout(() => {
            setVerificationStatus('');
            setIsOtpModalOpen(false);
            setMobileNumber('');
            setVerificationCode('');
          }, 2000);
        } else {
        // Handle API failure response
        if (data.error && data.error.message) {
          setVerificationStatus(data.error.message); // Display the specific error message from the API
        } else {
          setVerificationStatus(data.message || '認証に失敗しました'); // Fallback message if no error.message but not ok
        }
        setTimeout(() => setVerificationStatus(''), 3000);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setVerificationStatus('エラーが発生しました。もう一度お試しください。');
      setTimeout(() => setVerificationStatus(''), 2000);
    } finally {
      setIsLoadingVerification(false); // Reset loading state
    }
  };

  const handleOpenMobileModal = () => {
    handleVerifyModalClose(); // Close the initial prompt
    handleMobileNumModelOpen(); // Open the mobile number input modal
    setMobileNumber(''); // Reset mobile number
    setOtpMessage(''); // Clear any previous messages
  };

  const handleOtpModalClose = () => {
    setIsOtpModalOpen(false);
    setVerificationCode(''); // Clear OTP when modal closes
    setVerificationStatus(''); // Clear status when modal closes
    // If user closes OTP modal without completing, you might want to reopen
    // the mobile number modal, or just let them re-trigger the process.
    // For now, it just closes.
  };

  return (
    <>
      {/* Initial verification prompt */}
      <Modal open={isVerifyModalOpen} onClose={handleVerifyModalClose}>
        <Box sx={{ ...modalBoxStyle }}>
          <AlertIcon style={{ marginBottom: '10px' }} />
          <Typography variant="h6" sx={modalTextStyle}>
            本人認証をしてからご応募ください
          </Typography>
          <Button onClick={handleOpenMobileModal} variant="contained">認証する</Button>
          <div style={closeIconStyle}>
            <Cancel onClick={handleVerifyModalClose} />
          </div>
        </Box>
      </Modal>

      {/* Mobile number input Modal */}
      <Modal
        open={isMobileNumModelOpen}
        onClose={() => {
          setOtpMessage('');
          setMobileNumber('');
          handleMobileNumModelClose();
        }}
      >
        <Box sx={{ ...modalBoxStyle }}>
          <Typography variant="h6" sx={modalTextStyle}>本人確認画面</Typography>
          <Typography sx={subModalTextStyle}>
            本人確認がまだ完了していません<br/>
            認証後に正式応募することができます
          </Typography>
          <Typography
            sx={secondSubModalTextStyle}>
            ※2回目以降の手続きは不要
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              mb: '4px',
              textAlign:'left',
              fontFamily: '"Noto Sans JP", sans-serif'
            }}
          >
            認証コードの送信先
          </Typography>
          <TextField
            variant="outlined"
            type="text"
            placeholder="携帯番号"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            size="small"
            sx={{ marginBottom: '20px', width:'100%' }}
          />
          <Typography
            sx={{
              fontSize: '13px',
              color: '#666',
              textAlign: 'center',
              mb: 3,
              fontFamily: '"Noto Sans JP", sans-serif'
            }}>
            一度登録した番号を他者が使用した場合、再度の本人確認が必要になる場合があります
          </Typography>
          <Button variant="contained" onClick={handleSendOtp} disabled={isLoadingOtp}>
            {isLoadingOtp ? '送信中...' : 'コードの送信​'}
          </Button>
          {otpMessage && (
            <Typography
              color={otpMessage.includes('エラー') || otpMessage.includes('失敗') || otpMessage.includes('不足') ? "error" : "primary"}
              sx={{ fontSize: '12px', marginBottom: '8px', marginTop:'5px' }}
            >
              {otpMessage}
            </Typography>
          )}
          <div style={closeIconStyle}>
            <Cancel onClick={handleMobileNumModelClose} />
          </div>
        </Box>
      </Modal>

      {/* NEW: OTP verification Modal */}
      <Modal
        open={isOtpModalOpen}
        onClose={() => {
          setVerificationStatus('');
          setVerificationCode('');
          setIsOtpModalOpen(false);
        }}
      >
        <Box sx={{ ...modalBoxStyle }}>
          {/* <Typography variant="h6" sx={{ ...modalTextStyle, marginBottom: '10px' }}>携帯番号へ認証コードを送る</Typography> */}
          <Typography variant="h6" sx={modalTextStyle}>本人確認画面</Typography>
          <Typography sx={subModalTextStyle}>
            本人確認がまだ完了していません​<br/>
            認証後に正式応募することができます​
          </Typography>
          <TextField
            variant="outlined"
            type="text"
            placeholder="認証コード"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            size="small"
            sx={{ marginBottom: '20px' }}
          />
          <Button variant="contained" onClick={handleVerifyCode} disabled={isLoadingVerification}>
            {isLoadingVerification ? '確認中...' : '確認'}
          </Button>
          {verificationStatus && (
            <Typography
              color={verificationStatus.includes('失敗') || verificationStatus.includes('エラー') ? "error" : "primary"}
              sx={{ fontSize: '12px', marginBottom: '8px', marginTop:'5px' }}
            >
              {verificationStatus}
            </Typography>
          )}
          <div style={closeIconStyle}>
            <Cancel onClick={handleOtpModalClose} />
          </div>
        </Box>
      </Modal>
    </>
  );
};

const modalBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '15px',
  textAlign: 'center',
  p: 4,
};

const modalTextStyle = {
  marginBottom: '20px',
  fontSize: '18px',
  fontWeight: '400',
};

const subModalTextStyle = {
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'center',
  mb: 1,
  fontFamily: '"Noto Sans JP", sans-serif'
}

const secondSubModalTextStyle = {
  fontSize: '12px',
  fontWeight: 500,
  textAlign: 'center',
  mb: 2,
  fontFamily: '"Noto Sans JP", sans-serif'
}

const closeIconStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
  padding: '10px',
  cursor: 'pointer',
};

export default PhoneVerificationModal;